import { CalendarCheck, Heart, Lock, MessageCircle, Play, Star } from "lucide-react";
import { Logo } from "@/components/brand/Logo";

/* Illustrative interfaces that show each service doing its job. They carry
   no real client data and are hidden from assistive tech (the copy says it). */

const TILE_STYLES = [
  "bg-brand-primary text-white",
  "bg-teal-soft text-brand-primary",
  "bg-brand-secondary text-brand-primary",
  "bg-white text-brand-primary border border-brand-primary/10",
  "bg-navy-deep text-teal-light",
  "bg-teal-soft text-teal-strong",
];

export function PhoneProfileMock({
  handle,
  bookLabel,
  highlights,
  tiles,
  message,
}: {
  handle: string;
  bookLabel: string;
  highlights: readonly string[];
  tiles: readonly string[];
  message: string;
}) {
  return (
    <div aria-hidden="true" className="relative mx-auto w-full max-w-[21rem] select-none transition-[translate,rotate] duration-700 ease-[var(--ease-out-expo)] motion-safe:hover:-translate-y-1.5 motion-safe:hover:-rotate-1">
      <div className="absolute -inset-10 rounded-full bg-[radial-gradient(circle,rgb(114_181_177/0.35),transparent_65%)]" />
      <div className="relative rounded-[2.75rem] border-[10px] border-brand-primary bg-white p-4 pt-7 shadow-lit-lg">
        <div className="absolute left-1/2 top-2.5 h-4 w-20 -translate-x-1/2 rounded-full bg-brand-primary" />
        <div className="flex items-center gap-4">
          <span className="grid size-16 shrink-0 place-items-center rounded-full bg-gradient-to-br from-brand-secondary to-brand-primary p-[3px]">
            <span className="grid size-full place-items-center rounded-full bg-white">
              <Logo layout="mark" alt="" className="w-9" />
            </span>
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-brand-primary">{handle}</p>
            <div className="mt-2 space-y-1.5">
              <span className="block h-1.5 w-32 rounded-full bg-brand-primary/15" />
              <span className="block h-1.5 w-24 rounded-full bg-brand-primary/10" />
            </div>
          </div>
        </div>
        <span className="mt-4 flex min-h-9 items-center justify-center gap-2 rounded-lg bg-brand-secondary text-xs font-semibold text-brand-primary">
          <CalendarCheck className="size-3.5" />
          {bookLabel}
        </span>
        <div className="mt-4 flex justify-between">
          {highlights.map((label) => (
            <span key={label} className="flex w-14 flex-col items-center gap-1">
              <span className="size-11 rounded-full border-2 border-brand-secondary/70 bg-teal-soft" />
              <span className="w-full truncate text-center text-[0.6rem] text-ink-muted">{label}</span>
            </span>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-3 gap-1">
          {tiles.map((label, i) => (
            <span
              key={`${label}-${i}`}
              className={`grid aspect-square place-items-center rounded-md p-1.5 text-center font-display text-[0.7rem] font-semibold leading-tight ${TILE_STYLES[i % TILE_STYLES.length]}`}
            >
              {label}
            </span>
          ))}
        </div>
      </div>
      <div className="float-soft absolute -left-6 bottom-16 flex max-w-[13rem] items-start gap-2.5 rounded-2xl bg-white p-3 shadow-lit-lg sm:-left-12">
        <span className="grid size-7 shrink-0 place-items-center rounded-full bg-teal-soft text-teal-strong">
          <MessageCircle className="size-3.5" />
        </span>
        <span className="text-xs leading-snug text-brand-primary">{message}</span>
      </div>
      <div
        className="float-soft absolute -right-4 top-28 grid size-11 place-items-center rounded-full bg-white text-brand-secondary shadow-lit-lg sm:-right-8"
        style={{ ["--delay" as string]: "1.2s" }}
      >
        <Heart className="size-5 fill-current" />
      </div>
    </div>
  );
}

export function BrowserBookingMock({
  domain,
  headline,
  bookTitle,
  days,
  slots,
  confirm,
  reviews,
}: {
  domain: string;
  headline: string;
  bookTitle: string;
  days: readonly string[];
  slots: readonly string[];
  confirm: string;
  reviews: string;
}) {
  return (
    <div aria-hidden="true" className="relative mx-auto w-full max-w-[34rem] select-none transition-[translate,rotate] duration-700 ease-[var(--ease-out-expo)] motion-safe:hover:-translate-y-1.5 motion-safe:hover:-rotate-1">
      <div className="absolute -inset-10 rounded-full bg-[radial-gradient(circle,rgb(114_181_177/0.32),transparent_65%)]" />
      <div className="relative overflow-hidden rounded-2xl border border-brand-primary/12 bg-white shadow-lit-lg">
        <div className="flex items-center gap-2 border-b border-brand-primary/8 bg-brand-bg px-4 py-3">
          <span className="size-2.5 rounded-full bg-brand-primary/20" />
          <span className="size-2.5 rounded-full bg-brand-primary/20" />
          <span className="size-2.5 rounded-full bg-brand-primary/20" />
          <span className="ml-3 flex flex-1 items-center gap-1.5 rounded-full bg-white px-3 py-1 text-[0.7rem] text-ink-muted">
            <Lock className="size-3" />
            {domain}
          </span>
        </div>
        <div className="grid gap-5 p-6 sm:grid-cols-[1fr_1.1fr]">
          <div>
            <p className="font-display text-xl font-semibold leading-tight text-brand-primary">{headline}</p>
            <div className="mt-3 space-y-1.5">
              <span className="block h-1.5 w-full rounded-full bg-brand-primary/10" />
              <span className="block h-1.5 w-4/5 rounded-full bg-brand-primary/10" />
            </div>
            <p className="mt-4 flex items-center gap-1 text-[0.7rem] font-semibold text-brand-primary">
              {Array.from({ length: 5 }, (_, i) => (
                <Star key={i} className="size-3 fill-brand-secondary text-brand-secondary" />
              ))}
              <span className="ml-1 text-ink-muted">{reviews}</span>
            </p>
          </div>
          <div className="rounded-xl border border-brand-primary/10 p-3.5">
            <p className="text-xs font-semibold text-brand-primary">{bookTitle}</p>
            <div className="mt-2.5 grid grid-cols-5 gap-1">
              {days.map((day, i) => (
                <span
                  key={day}
                  className={`rounded-md py-1 text-center text-[0.62rem] font-semibold ${i === 3 ? "bg-brand-primary text-white" : "bg-brand-bg text-ink-muted"}`}
                >
                  {day}
                </span>
              ))}
            </div>
            <div className="mt-2.5 grid grid-cols-3 gap-1">
              {slots.map((slot, i) => (
                <span
                  key={slot}
                  className={`rounded-md border py-1 text-center text-[0.62rem] font-semibold ${i === 4 ? "border-brand-secondary bg-teal-soft text-brand-primary" : "border-brand-primary/10 text-ink-muted"}`}
                >
                  {slot}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="float-soft absolute -bottom-6 left-6 flex items-center gap-2.5 rounded-2xl bg-white px-4 py-3 shadow-lit-lg">
        <span className="grid size-8 place-items-center rounded-full bg-brand-secondary text-brand-primary">
          <CalendarCheck className="size-4" />
        </span>
        <span className="text-xs font-semibold text-brand-primary">{confirm}</span>
      </div>
    </div>
  );
}

export function VideoAdvisoryMock({ title, note, chapters }: { title: string; note: string; chapters: readonly string[] }) {
  return (
    <div aria-hidden="true" className="relative mx-auto w-full max-w-[32rem] select-none transition-[translate,rotate] duration-700 ease-[var(--ease-out-expo)] motion-safe:hover:-translate-y-1.5 motion-safe:hover:-rotate-1">
      <div className="absolute -inset-10 rounded-full bg-[radial-gradient(circle,rgb(114_181_177/0.32),transparent_65%)]" />
      <div className="relative overflow-hidden rounded-2xl bg-brand-primary shadow-lit-lg">
        <div className="light-pool-navy relative grid aspect-video place-items-center [--pool-x:70%] [--pool-y:30%]">
          <Logo layout="mark" alt="" className="absolute left-5 top-5 w-10 opacity-80" />
          <span className="grid size-16 place-items-center rounded-full bg-white/95 text-brand-primary shadow-lit-lg">
            <Play className="ml-1 size-7 fill-current" />
          </span>
          <p className="absolute bottom-4 left-5 right-5 font-display text-lg font-semibold text-white">{title}</p>
        </div>
        <div className="h-1 bg-white/10">
          <div className="h-full w-2/5 bg-brand-secondary" />
        </div>
        <ul className="grid gap-px bg-white/10 sm:grid-cols-3">
          {chapters.map((chapter) => (
            <li key={chapter} className="bg-brand-primary px-4 py-3 text-xs text-on-navy-muted">
              {chapter}
            </li>
          ))}
        </ul>
      </div>
      <p className="float-soft absolute -bottom-5 right-6 flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-xs font-semibold text-brand-primary shadow-lit-lg">
        <Lock className="size-3.5 text-teal-strong" />
        {note}
      </p>
    </div>
  );
}
